import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ListPlus, Link2, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

// Strip leading list markers: "1.", "1)", "(1)", "1 -", bullets (•, -, *, #), trailing noise.
export function parseNameList(raw) {
  return (raw || "")
    .split(/\r?\n/)
    .map((line) =>
      line
        .replace(/^\s*[([{]?\s*\d+\s*[)\].:\-–]?\s*/, "") // leading numbers "1." "1)" "(1)"
        .replace(/^\s*[•‣◦⁃∙·\-*#>→]+\s*/, "")            // leading bullets / dashes
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean);
}

export default function ImportPlayersModal({ open, onClose, onImport }) {
  const [listText, setListText] = useState("");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const names = parseNameList(listText);

  const importList = () => {
    if (!names.length) { toast.error("Paste at least one name (one per line)."); return; }
    const added = onImport(names);
    setListText("");
    toast.success(`${added} player${added === 1 ? "" : "s"} imported.`);
    onClose();
  };

  const importReclub = () => {
    if (!url.trim()) { toast.error("Paste a Reclub meet link."); return; }
    setLoading(true);
    api.post("/openplay/import-reclub", { url: url.trim() })
      .then(({ data }) => {
        const added = onImport(data.players || []);
        toast.success(`${added} player${added === 1 ? "" : "s"} imported from "${data.event || "Reclub"}".`);
        setUrl("");
        onClose();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not import from Reclub."))
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass max-w-lg" style={{ background: "var(--surface)" }} data-testid="import-players-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Download className="w-5 h-5 text-primary-c" /> Import players</DialogTitle>
          <DialogDescription style={{ color: "var(--text-2)" }}>Bulk-add players from a pasted list or a Reclub meet.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="list" className="mt-2">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="list" data-testid="import-tab-list"><ListPlus className="w-4 h-4 mr-1.5" /> Paste list</TabsTrigger>
            <TabsTrigger value="reclub" data-testid="import-tab-reclub"><Link2 className="w-4 h-4 mr-1.5" /> Reclub link</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="space-y-3 mt-4">
            <Textarea data-testid="import-list-textarea" rows={8} value={listText} onChange={(e) => setListText(e.target.value)}
              placeholder={"One player per line, e.g.\n1. Alex Cruz\n2) Jamie Lee\n• Sam Dizon\nMaria Santos"}
              className="bg-surface glow-border focus:ring-2 focus:ring-lime-400 font-mono text-sm" />
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: "var(--text-2)" }} data-testid="import-list-count">
                {names.length} name{names.length === 1 ? "" : "s"} detected · numbers & bullets removed
              </span>
              <button data-testid="import-list-submit" onClick={importList} disabled={!names.length}
                className="btn-glow rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50">Import {names.length || ""}</button>
            </div>
          </TabsContent>

          <TabsContent value="reclub" className="space-y-3 mt-4">
            <Input data-testid="import-reclub-url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://reclub.co/m/XXXXXX" className="bg-surface glow-border focus:ring-2 focus:ring-lime-400" />
            <p className="text-xs" style={{ color: "var(--text-2)" }}>
              Paste the public Reclub meet link — we'll pull the confirmed players. Guests without a profile may need adding by hand.
            </p>
            <button data-testid="import-reclub-submit" onClick={importReclub} disabled={loading}
              className="btn-glow rounded-full px-5 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Import from Reclub
            </button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
